import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from 'ui-box';
import {
  majorScale,
  defaultFills,
  Button,
  FormField,
  InputGroup,
} from '../../../npaper';
import { AddressFormControls } from '../../../npaper/AddressFormControls';
import {
  IOrganisationSnapshotIn,
  IOrganisation,
  useStores,
} from '../../../stores';
import { AuthTitle } from '../AuthLayout';

type OrganisationFormValues = IOrganisationSnapshotIn;

const OrganisationStep = ({
  onUpdate,
  onBack,
}: {
  onUpdate: (organisation: IOrganisation) => void;
  onBack: () => void;
}) => {
  const { companyStore, organisationStore, authStore } = useStores();
  const intl = useIntl();
  const formProps = useForm<OrganisationFormValues>();
  const {
    register,
    watch,
    getValues,
    setValue,
    setError,
    handleSubmit,
    formState,
  } = formProps;

  // TODO: Handle errors

  const [vatValid, setVatValid] = useState<boolean | null>(null);
  const [validatingVat, setValidatingVat] = useState(false);
  const vatNumber = watch('vat_number');

  const validateVat = React.useCallback(() => {
    if (!vatNumber || vatNumber.length <= 2) {
      return;
    }
    setValidatingVat(true);
    companyStore
      .validateVatNumber(vatNumber)
      .then((res) => {
        setValidatingVat(false);
        setVatValid(res.valid);
        if (res.valid) {
          const values = getValues();
          setValue('name', values.name || res.name);
          setValue('name', values.name || res.name);
          setValue('address.country', res.country);
          setValue(
            'address.address_line1',
            (values.address && values.address.address_line1) || res.address
          );
          setValue(
            'address.postal_code',
            (values.address && values.address.postal_code) || res.postal_code
          );
          setValue(
            'address.city',
            (values.address && values.address.city) || res.city
          );
        }
      })
      .catch((err) => {
        if (
          err.response &&
          err.response.data &&
          err.response.data['vat_number']
        ) {
          const errorString = err.response.data['vat_number'];
          if (errorString === 'INVALID_INPUT') {
            setError('vat_number', {
              message: intl.formatMessage({
                defaultMessage:
                  'Invalid input for VAT number. Please prepend the VAT number with a country code, e.g. DK12345678.',
              }),
            });
          }
        }
        setValidatingVat(false);
        setVatValid(false);
      });
  }, [companyStore, getValues, intl, setError, setValue, vatNumber]);

  const onSubmit = async (values: OrganisationFormValues) => {
    try {
      const organisation = await organisationStore.createItem(values);
      authStore.setCurrentOrganisation(organisation!);
      onUpdate(organisation);
    } catch (err) {
      return;
    }
  };

  return (
    <Box>
      <FormProvider {...formProps}>
        <AuthTitle
          title={intl.formatMessage({
            defaultMessage: 'Your business',
          })}
          sub={intl.formatMessage(
            {
              defaultMessage: 'Step {step} of {maxSteps}',
              description: 'Step progress hint above heading',
            },
            {
              step: 2,
              maxSteps: 3,
            }
          )}
          description={intl.formatMessage({
            defaultMessage:
              "Tell us a bit about your business. Then we'll help you create correct invoices.",
          })}
        />

        <FormField
          label={intl.formatMessage({
            defaultMessage: 'What is your VAT number?',
          })}
          marginBottom={majorScale(2)}
          large
        >
          <InputGroup
            placeholder="DK12345678"
            name="vat_number"
            ref={register}
            isLoading={validatingVat}
            iconRight={
              vatValid ? 'tick' : vatValid === false ? 'error' : undefined
            }
            iconColor={vatValid ? defaultFills.success[500] : undefined}
            validationMessage={
              vatValid === false
                ? intl.formatMessage({
                    defaultMessage:
                      'VAT number is not a valid EU VAT number according to the RIES database. You can continue for now, but you should correct the VAT number if you have a valid VAT number.',
                    description: 'Validation: Vat invalid message',
                  })
                : undefined
            }
            onBlur={() => {
              validateVat();
            }}
          />
        </FormField>

        <FormField
          label={intl.formatMessage({
            defaultMessage: 'What is the name of your business?',
          })}
          marginBottom={majorScale(2)}
          large
        >
          <InputGroup
            placeholder={intl.formatMessage({
              defaultMessage: 'Business name',
            })}
            name="name"
            ref={register({
              required: intl.formatMessage({
                defaultMessage: 'We need to know your legal business name',
                description: 'Validation: Business name required',
              }),
            })}
          />
        </FormField>

        <FormField
          label={intl.formatMessage({
            defaultMessage: 'What is the address of your business?',
          })}
          marginBottom={majorScale(2)}
          large
        >
          <AddressFormControls name="address" />
        </FormField>

        <Box
          display="flex"
          flexWrap="wrap"
          justifyContent="space-between"
          marginTop={majorScale(2)}
        >
          <Button
            minimal
            iconLeft="arrow-left"
            onClick={() => onBack()}
            disabled={formState.isSubmitting}
          >
            <FormattedMessage id="defaults.back" defaultMessage="Back" />
          </Button>
          <Button
            primary
            minWidth="50%"
            onClick={handleSubmit(onSubmit)}
            isLoading={formState.isSubmitting}
          >
            <FormattedMessage
              id="defaults.continue"
              defaultMessage="Continue"
            />
          </Button>
        </Box>
      </FormProvider>
    </Box>
  );
};

export default OrganisationStep;
